import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Save,
  FileDown,
  FileType,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Printer,
  Share2,
} from "lucide-react";
import TemplateSelector from "../../components/Resume/TemplateSelector";
import ProgressSteps from "../../components/Resume/ProgressSteps";
import FormNavigation from "../../components/Resume/FormNavigation";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import * as resumeService from "../../services/resumeService";
import { AuthContext } from "../../context/AuthContext";
import PersonalInfoForm from "../../components/Resume/Sections/PersonalInfoForm";
import ExperienceForm from "../../components/Resume/Sections/ExperienceForm";
import EducationForm from "../../components/Resume/Sections/EducationForm";
import SkillsForm from "../../components/Resume/Sections/SkillsForm";
import ProjectsForm from "../../components/Resume/Sections/ProjectsForm";
import CertificationsForm from "../../components/Resume/Sections/CertificationsForm";
import ResumePreview from "../../components/Resume/ResumePreview";
import { exportToPDF } from "../../utils/exportPDF";
import { exportToDOCX } from "../../utils/exportDOCX";

const steps = [
  "Template",
  "Personal Info",
  "Experience",
  "Education",
  "Skills",
  "Projects",
  "Certifications",
  "Review",
];

const emptyResume = {
  title: "",
  templateType: "modern",
  sections: {
    personalInfo: {},
    experience: [],
    education: [],
    skills: [],
    projects: [],
    certifications: [],
    customSections: [],
  },
};

export default function ResumeBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { credits } = useContext(AuthContext);

  const [currentStep, setCurrentStep] = useState(0);
  const [resumeData, setResumeData] = useState(emptyResume);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [activeTab, setActiveTab] = useState("form"); // for mobile
  const [resumeId, setResumeId] = useState(id || null);
  const [zoom, setZoom] = useState(0.85);
  const [publicLink, setPublicLink] = useState("");
  const [exporting, setExporting] = useState(false);
  const saveTimer = useRef(null);
  const initialized = useRef(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!id) {
        initialized.current = true;
        return;
      }
      try {
        const res = await resumeService.getResumeById(id);
        if (mounted) {
          setResumeData({
            title: res.resume?.title || "",
            templateType: res.resume?.templateType || "modern",
            sections: res.resume?.sections || emptyResume.sections,
          });
          setResumeId(id);
        }
      } catch (err) {
        toast.error("Failed to load resume");
        navigate("/resumes");
      } finally {
        if (mounted) {
          setLoading(false);
          initialized.current = true;
        }
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Autosave debounce 2s
  useEffect(() => {
    if (!initialized.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      handleSave().catch(() => {});
    }, 2000);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeData, currentStep]);

  const updateSection = (sectionName, data) => {
    setResumeData((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionName]: data,
      },
    }));
  };

  async function handleSave() {
    setIsSaving(true);
    try {
      if (!resumeId) {
        if (!resumeData.title.trim()) {
          // Avoid creating unnamed resumes
          return;
        }
        const res = await resumeService.createResume(resumeData);
        setResumeId(res.resume?._id);
      } else {
        await resumeService.updateResume(resumeId, resumeData);
      }
      setLastSaved(new Date());
    } catch {
      toast.error("Save failed");
    } finally {
      setIsSaving(false);
    }
  }

  function requiredForStepValid(stepIdx) {
    if (stepIdx === 0) return !!resumeData.templateType;
    if (stepIdx === 1)
      return (
        !!resumeData.sections.personalInfo?.name && !!resumeData.title.trim()
      );
    return true;
  }

  function nextStep() {
    if (!requiredForStepValid(currentStep)) {
      toast.error("Please complete required fields before proceeding.");
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentStep((s) => Math.min(steps.length - 1, s + 1));
  }

  function prevStep() {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentStep((s) => Math.max(0, s - 1));
  }

  async function exportFile(type) {
    try {
      setExporting(true);
      const name = (
        resumeData.title?.trim() ? resumeData.title.trim() : "resume"
      ).replace(/[^a-z0-9-_]+/gi, "_");
      if (type === "pdf") {
        await exportToPDF("resume-preview", `${name}.pdf`);
        toast.success("PDF downloaded");
      } else {
        await exportToDOCX(resumeData, `${name}.docx`);
        toast.success("DOCX downloaded");
      }
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  }

  async function togglePublic() {
    if (!resumeId) {
      toast.info("Please save the resume first");
      return;
    }
    try {
      const res = await resumeService.togglePublic(resumeId);
      const link = res?.resume?.shareableLink || "";
      setPublicLink(link ? `${window.location.origin}/resume/${link}` : "");
      toast.success(
        res?.resume?.isPublic ? "Resume is now public" : "Resume is now private"
      );
    } catch {
      toast.error("Failed to toggle public status");
    }
  }

  const savingText = useMemo(() => {
    if (isSaving) return "Saving...";
    if (lastSaved) return `Saved ${lastSaved.toLocaleTimeString()}`;
    return "";
  }, [isSaving, lastSaved]);

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <div className="sticky top-0 z-30 border-b bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-2 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-0">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-xs sm:text-sm text-gray-700 hover:text-primary-600 whitespace-nowrap"
            >
              <ArrowLeft className="w-4 h-4 flex-shrink-0" /> Back
            </Link>
            <input
              className="flex-1 min-w-0 text-sm sm:text-base font-semibold border-b border-transparent focus:border-primary-600 focus:outline-none"
              value={resumeData.title}
              onChange={(e) =>
                setResumeData((r) => ({ ...r, title: e.target.value }))
              }
              placeholder="Untitled Resume"
              aria-label="Resume title"
            />
          </div>
          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            <div className="hidden sm:block text-xs sm:text-sm text-gray-500">
              {savingText}
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSave}
              title="Save Resume"
            >
              <Save className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => exportFile("pdf")}
              disabled={exporting}
              title="Export as PDF"
            >
              <FileDown className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => exportFile("docx")}
              disabled={exporting}
              title="Export as DOCX"
            >
              <FileType className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile tabs */}
      <div className="sm:hidden sticky top-14 z-20 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 flex">
          <button
            className={`flex-1 py-2 ${
              activeTab === "form"
                ? "border-b-2 border-primary-600 text-primary-700"
                : "text-gray-600"
            }`}
            onClick={() => setActiveTab("form")}
          >
            Form
          </button>
          <button
            className={`flex-1 py-2 ${
              activeTab === "preview"
                ? "border-b-2 border-primary-600 text-primary-700"
                : "text-gray-600"
            }`}
            onClick={() => setActiveTab("preview")}
          >
            Preview
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        <ProgressSteps
          currentStep={currentStep}
          steps={steps}
          onStepClick={(s) => setCurrentStep(s)}
        />

        <div className="mt-4 sm:mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
          {/* Left: form */}
          <div
            className={`md:col-span-1 lg:col-span-2 ${
              activeTab === "preview" ? "hidden md:block lg:block" : ""
            }`}
          >
            {currentStep === 0 && (
              <Card title="Choose Template">
                <TemplateSelector
                  selectedTemplate={resumeData.templateType}
                  onSelectTemplate={(id) =>
                    setResumeData((r) => ({ ...r, templateType: id }))
                  }
                  userCredits={credits}
                  onPremiumConfirm={() =>
                    toast.info("2 credits will be charged on export")
                  }
                />
              </Card>
            )}
            {currentStep === 1 && (
              <Card title="Personal Information">
                <PersonalInfoForm
                  data={resumeData.sections.personalInfo}
                  onChange={(val) => updateSection("personalInfo", val)}
                />
              </Card>
            )}
            {currentStep === 2 && (
              <Card title="Experience">
                <ExperienceForm
                  experiences={resumeData.sections.experience}
                  onChange={(val) => updateSection("experience", val)}
                />
              </Card>
            )}
            {currentStep === 3 && (
              <Card title="Education">
                <EducationForm
                  education={resumeData.sections.education}
                  onChange={(val) => updateSection("education", val)}
                />
              </Card>
            )}
            {currentStep === 4 && (
              <Card title="Skills">
                <SkillsForm
                  skills={resumeData.sections.skills}
                  onChange={(val) => updateSection("skills", val)}
                />
              </Card>
            )}
            {currentStep === 5 && (
              <Card title="Projects">
                <ProjectsForm
                  projects={resumeData.sections.projects}
                  onChange={(val) => updateSection("projects", val)}
                />
              </Card>
            )}
            {currentStep === 6 && (
              <Card title="Certifications">
                <CertificationsForm
                  certifications={resumeData.sections.certifications}
                  onChange={(val) => updateSection("certifications", val)}
                />
              </Card>
            )}
            {currentStep === 7 && (
              <Card title="Review">
                <p className="text-sm text-gray-600">
                  Review your entries on the right preview. You can go back to
                  adjust any section.
                </p>
              </Card>
            )}

            <FormNavigation
              onPrevious={prevStep}
              onNext={nextStep}
              isFirstStep={currentStep === 0}
              isLastStep={currentStep === steps.length - 1}
              canProceed={requiredForStepValid(currentStep)}
            />
          </div>

          {/* Right: preview */}
          <div
            className={`md:col-span-1 lg:col-span-3 ${
              activeTab === "form" ? "hidden md:block lg:block" : ""
            }`}
          >
            <Card
              title={
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                  <span className="text-sm sm:text-base">Live Preview</span>
                  <div className="flex items-center gap-1 overflow-x-auto">
                    <select
                      className="border rounded px-2 py-1 text-xs sm:text-sm whitespace-nowrap"
                      value={resumeData.templateType}
                      onChange={(e) =>
                        setResumeData((r) => ({
                          ...r,
                          templateType: e.target.value,
                        }))
                      }
                    >
                      <option value="modern">Modern</option>
                      <option value="professional">Professional</option>
                      <option value="creative">Creative</option>
                      <option value="minimal">Minimal</option>
                    </select>
                    <button
                      className="p-1 rounded hover:bg-gray-100"
                      onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))}
                      title="Zoom out"
                    >
                      <ZoomOut className="w-4 h-4" />
                    </button>
                    <button
                      className="p-1 rounded hover:bg-gray-100"
                      onClick={() => setZoom((z) => Math.min(1.5, z + 0.1))}
                      title="Zoom in"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </button>
                    <button
                      className="p-1 rounded hover:bg-gray-100"
                      onClick={() => setZoom(0.85)}
                      title="Fit"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                    <button
                      className="p-1 rounded hover:bg-gray-100"
                      onClick={() => window.print()}
                      title="Print"
                    >
                      <Printer className="w-4 h-4" />
                    </button>
                    <button
                      className="p-1 rounded hover:bg-gray-100"
                      onClick={togglePublic}
                      title="Share"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              }
            >
              <ResumePreview
                resumeData={resumeData}
                templateType={resumeData.templateType}
                zoom={zoom}
              />
              {publicLink && (
                <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center gap-2 text-sm">
                  <span className="text-gray-600 whitespace-nowrap">
                    Shareable Link:
                  </span>
                  <input
                    className="flex-1 min-w-0 border rounded px-2 py-1 text-xs sm:text-sm"
                    readOnly
                    value={publicLink}
                  />
                  <button
                    className="text-primary-600 underline whitespace-nowrap"
                    onClick={() => {
                      navigator.clipboard.writeText(publicLink);
                      toast.success("Link copied");
                    }}
                  >
                    Copy
                  </button>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
